import type React from "react";

import type { Cell, Column, Row, RowData } from "@tanstack/react-table-v9";

import { colorTheme } from "@utils";

import type { TableV2Features } from "../../features";

export type AnyColumn<TData extends RowData> = Column<
  TableV2Features,
  TData,
  unknown
>;

export type DropPlacement = "before" | "after";

export interface ColumnDragState {
  draggingId: string | null;
  target: { id: string; placement: DropPlacement } | null;
}

export const NO_DRAG: ColumnDragState = { draggingId: null, target: null };

export const columnLayout = <TData extends RowData>(
  column: AnyColumn<TData>,
  size: number,
  underline = false,
  fixedWidth = false,
): React.CSSProperties => {
  const pinned = column.getIsPinned();
  const fixed = !!pinned || fixedWidth || !!column.columnDef.meta?.fixedWidth;
  const lines = [
    !pinned && column.columnDef.meta?.separator && "inset 1px 0 0",
    underline && "inset 0 -1px 0",
  ].filter(Boolean);
  return {
    boxShadow: lines.length
      ? lines.map((line) => `${line} ${colorTheme("neutralL4")}`).join(", ")
      : undefined,
    // Unpinned columns stretch to fill the container; pinned ones keep a fixed width so sticky offsets line up
    // Virtualized columns can't stretch, or the spacers wouldn't line up
    flex: `${fixed ? 0 : size} 0 ${size}px`,
    minWidth: size,
    maxWidth: fixed ? size : undefined,
    insetInlineStart: pinned === "start" ? column.getStart("start") : undefined,
    insetInlineEnd: pinned === "end" ? column.getAfter("end") : undefined,
  };
};

export const pinEdges = <TData extends RowData>(column: AnyColumn<TData>) => {
  const pinned = column.getIsPinned();
  const table = column.table;
  return {
    $isLastStart:
      pinned === "start" &&
      column.getPinnedIndex() === table.getStartVisibleLeafColumns().length - 1,
    $isFirstEnd:
      pinned === "end" &&
      column.getPinnedIndex() === 0 &&
      column.columnDef.meta?.type !== "actions",
  };
};

export const orderedCells = <TData extends RowData>(
  row: Row<TableV2Features, TData>,
): Cell<TableV2Features, TData, unknown>[] => [
  ...row.getStartVisibleCells(),
  ...row.getCenterVisibleCells(),
  ...row.getEndVisibleCells(),
];
